import { ColumnDef } from '@tanstack/react-table';
import { Module } from '@/entities/Courses';
import { formatDateTime } from '@/shared/lib/utils/formatDate';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { Svg } from '@/shared/ui/Svg';
import { AppImage } from '@/shared/ui/AppImage';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import defaultAvatar from '@/shared/assets/images/default-avatar.png';


export const modulesColumns = (): ColumnDef<Module>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        uniqueKey="all-modules-select"
        name="all-modules"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        uniqueKey={`module-select-${row.original.id}`}
        name="module"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    size: 68,
  },
  {
    header: 'Module',
    accessorKey: 'title',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span
          className="block max-w-[260px] truncate text-black-headline"
          data-nav-title="true"
        >
          {row.original.title}
        </span>
      </div>
    ),
    size: 342,
  },
  {
    header: 'Module Lead',
    accessorKey: 'moduleLead',
    cell: ({ row }) => {
      const leadTeacher = row.original.moduleTeacher;
      if (!leadTeacher) {
        return <span className="text-grey-secondary">—</span>;
      }
      return (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9">
            <AppImage
              src={leadTeacher.avatar || defaultAvatar}
              alt={`${leadTeacher.firstName} ${leadTeacher.lastName}`}
              className="w-9 h-9 rounded-full object-cover"
            />
          </div>
          <span className="text-black-headline">{leadTeacher.username}</span>
        </div>
      );
    },
    size: 228,
  },
  {
    header: 'Lecturer',
    accessorKey: 'lecturer',
    cell: ({ row }) => {
      const lecturer = row.original.lecturer;
      if (!lecturer) {
        return <span className="text-grey-secondary">—</span>;
      }
      return (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9">
            <AppImage
              src={lecturer.avatar || defaultAvatar}
              alt={`${lecturer.firstName} ${lecturer.lastName}`}
              className="w-9 h-9 rounded-full object-cover"
            />
          </div>
          <span className="text-black-headline">{lecturer.username}</span>
        </div>
      );
    },
    size: 228,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Progress</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'progressPercent',
    cell: ({ row }) => {
      const progress = row.original.progressPercent ?? row.original.progress;
      return progress !== undefined ? (
        <span className="text-black-headline">{progress}%</span>
      ) : (
        <span className="text-grey-secondary">—</span>
      );
    },
    size: 122,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Attendance</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'attendancePercent',
    cell: ({ row }) => {
      const attendance = row.original.attendancePercent ?? row.original.attendance;
      return attendance !== undefined ? (
        <span className="text-black-headline">{attendance}%</span>
      ) : (
        <span className="text-grey-secondary">—</span>
      );
    },
    size: 140,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Last Activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'createdAt',
    cell: ({ row }) => {
      const dateString = row.original.createdAt || row.original.lastActivity;
      if (!dateString) {
        return <span className="text-grey-secondary">—</span>;
      }
      const lastActivity = formatDateTime(dateString);
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{lastActivity?.date}</span>
          <span className="text-grey-secondary text-sm">{lastActivity?.time}</span>
        </div>
      );
    },
    size: 144,
  },
  {
    id: 'tools',
    cell: ({ row, table }) => {
      const meta = table.options.meta as
        | { onEdit?: (module: Module) => void; onDelete?: (module: Module) => void }
        | undefined;
      return (
        <div className="flex items-center gap-2">
          {meta?.onEdit && (
            <button
              type="button"
              className="text-grey-secondary hover:text-black-headline transition-colors"
              aria-label="Edit module"
              onClick={() => meta.onEdit?.(row.original)}
            >
              <Svg Icon={EditIcon} width={20} height={20} />
            </button>
          )}
          {meta?.onDelete && (
            <button
              type="button"
              className="text-icon-dizlike hover:opacity-80 transition-colors"
              aria-label="Delete module"
              onClick={() => meta.onDelete?.(row.original)}
            >
              <Svg Icon={TrashIcon} width={20} height={20} />
            </button>
          )}
        </div>
      );
    },
    size: 108,
  },
];
